/**
 * Shared display copy for the planning-pipeline phases (contract doc 31 §2.3/§2.4/§10.4).
 * Single source of truth used by BOTH consumers: the live plan-progress-card phase list
 * (tooltips) and the plan-debug-dialog "Phases" section (labels + meaning) — so the
 * phase labels never drift between the two surfaces.
 */

import type { BlackboardPhase } from "../../../model/block.types";

/** Display copy for one phase of the pipeline (doc 31 §2.3 catalog). */
export interface PlanPhaseInfo {
  /** Short human name — phase list row in plan-progress-card, "Phases" section in plan-debug-dialog. */
  label: string;
  /** One-line English description of what the phase does (tooltip copy). */
  meaning: string;
  /**
   * Material icon ligature identifying the phase (doc 32 §2.2 identity map) — the single
   * source for both cards' phase chips. `plan_triage` is our own pick: doc 32 omits the triage,
   * and `traffic` reads as its decision light.
   */
  icon: string;
}

/**
 * Display copy per phase id, keyed by the raw wire id (snake_case as it travels). Covers BOTH
 * vocabularies of doc 31 §10.4: the 5 `PlanPhase` record keys of `Plan.phases` AND the 8 SSE
 * `PhaseId` catalog values that map onto them — `plan_router` has no phase record; the F1-F3
 * phases (`semantic_decomposition`/`capability_resolution`/`provider_binding`) map to the 3
 * contract records; `plan_triage`/`plan_assessment` map to the `plan_assessment` record;
 * `plan_run`/`run_engine` map to the `run_engine` record.
 */
export const PLAN_PHASE_INFO: Readonly<Record<BlackboardPhase, PlanPhaseInfo>> = {
  plan_router: {
    label: "Router",
    meaning: "Routes the request into the planning pipeline",
    icon: "alt_route",
  },
  semantic_decomposition: {
    label: "Decomposition",
    meaning: "Translates the request objective into semantic steps",
    icon: "account_tree",
  },
  capability_resolution: {
    label: "Capabilities",
    meaning: "Matches each task to candidate capabilities",
    icon: "category",
  },
  provider_binding: {
    label: "Binding",
    meaning: "Binds tasks to concrete resolver providers",
    icon: "link",
  },
  plan_triage: {
    label: "Gate",
    meaning: "Decides whether the plan proceeds, needs feedback or a replan",
    icon: "traffic",
  },
  plan_assessment: {
    label: "Assessment",
    meaning: "Collects facts, profiles the plan and runs validators",
    icon: "fact_check",
  },
  plan_run: {
    label: "Run prep",
    meaning: "Prepares the run configuration and closes the run",
    icon: "play_circle",
  },
  run_engine: {
    label: "Engine",
    meaning: "Executes the tasks and produces the outcome",
    icon: "bolt",
  },
};
